import * as Dialog from '@radix-ui/react-dialog'
import * as Dropdown from '@radix-ui/react-dropdown-menu'
import * as RTooltip from '@radix-ui/react-tooltip'
import { AlertTriangle, X } from '@/design-system/icons'
import { useState, type ReactNode } from 'react'
import { useT } from '@/i18n'
import { ScanToCloseCapture, useScanToClose } from './scanToClose'
import { Button, cn, Icon, IconButton, type IconComponent } from './primitives'

/* ------------------------------------------------------------------ Modal */

export interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /**
   * Scanning screens: two shots of the barcode scanner close this, so the operator never has to walk back to the
   * keyboard (see `useScanToClose`). Off everywhere else — a dialog nobody scans at does not need it.
   */
  scanToClose?: boolean
}

const modalWidths = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' }

export function Modal({ open, onOpenChange, title, description, children, footer, size = 'md', scanToClose }: ModalProps) {
  const { t } = useT()
  const scan = useScanToClose({ open: Boolean(scanToClose) && open, onClose: () => onOpenChange(false) })
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <Dialog.Content
          className={cn(
            'fixed start-1/2 top-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-1.5rem)] -translate-y-1/2 flex-col rounded-2xl border border-border bg-surface shadow-[0_24px_60px_-20px_rgba(16,24,40,0.35)] data-[state=open]:animate-scale-in ltr:-translate-x-1/2 rtl:translate-x-1/2',
            modalWidths[size],
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-border px-5 pb-3.5 pt-4">
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-bold text-fg-strong">{title}</Dialog.Title>
              {description ? <Dialog.Description className="mt-1 text-sm text-fg-muted">{description}</Dialog.Description> : <Dialog.Description className="sr-only">{title}</Dialog.Description>}
            </div>
            <Dialog.Close asChild>
              <IconButton label={t('common.close')} icon={X} size="sm" />
            </Dialog.Close>
          </div>
          {children && <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>}
          {/* the scanner closes it from across the room; the capture lives inside so the focus trap keeps every shot here */}
          {scanToClose && (
            <div className="space-y-2 px-5 pb-3">
              <ScanToCloseCapture scan={scan} />
              <p className="text-2xs text-fg-subtle">{scan.hint}</p>
            </div>
          )}
          {footer && <div className="flex items-center justify-end gap-2 rounded-b-2xl border-t border-border bg-bg-subtle/60 px-5 py-3">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/* ----------------------------------------------------------------- Drawer */

export function Drawer({ open, onOpenChange, title, description, children, footer, width = 'md' }: Omit<ModalProps, 'size'> & { width?: 'sm' | 'md' | 'lg' | 'xl' }) {
  const { t } = useT()
  const widths = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl', xl: 'sm:max-w-4xl' }
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-fade-in" />
        <Dialog.Content className={cn('fixed inset-y-0 end-0 z-50 flex w-full flex-col border-s border-border bg-surface-raised shadow-lg data-[state=open]:animate-slide-in-end', widths[width])}>
          <div className="flex items-start justify-between gap-4 border-b border-border px-5 pb-4 pt-[max(1rem,env(safe-area-inset-top))]">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-lg font-bold text-fg-strong">{title}</Dialog.Title>
              {description ? <Dialog.Description className="mt-0.5 text-sm text-fg-muted">{description}</Dialog.Description> : <Dialog.Description className="sr-only">{title}</Dialog.Description>}
            </div>
            <Dialog.Close asChild>
              <IconButton label={t('common.close')} icon={X} size="sm" />
            </Dialog.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-surface-raised px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/* ---------------------------------------------------------- ConfirmDialog */

export interface ConfirmOptions {
  title: ReactNode
  description?: ReactNode
  confirmLabel?: string
  tone?: 'danger' | 'primary'
  /** Typed confirmation for destructive bulk operations. */
  requireText?: string
}

export function ConfirmDialog({ open, onOpenChange, options, onConfirm, scanToClose }: { open: boolean; onOpenChange: (o: boolean) => void; options: ConfirmOptions; onConfirm: () => Promise<unknown> | void; scanToClose?: boolean }) {
  const { t } = useT()
  const [busy, setBusy] = useState(false)
  const [typed, setTyped] = useState('')
  const blocked = Boolean(options.requireText) && typed.trim() !== options.requireText
  return (
    <Modal
      scanToClose={scanToClose}
      open={open}
      onOpenChange={(o) => {
        if (!busy) {
          onOpenChange(o)
          setTyped('')
        }
      }}
      size="sm"
      title={
        <span className="flex items-center gap-2">
          {options.tone !== 'primary' && <Icon icon={AlertTriangle} size={18} className="text-danger" />}
          {options.title}
        </span>
      }
      description={options.description}
      footer={
        <>
          <Button variant="secondary" disabled={busy} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            variant={options.tone === 'primary' ? 'primary' : 'danger'}
            loading={busy}
            disabled={blocked}
            onClick={async () => {
              setBusy(true)
              try {
                await onConfirm()
                onOpenChange(false)
                setTyped('')
              } finally {
                setBusy(false)
              }
            }}
          >
            {options.confirmLabel ?? t('common.confirm')}
          </Button>
        </>
      }
    >
      {options.requireText && (
        <input
          className="h-10 w-full rounded-[10px] border border-border bg-surface px-3 font-mono text-sm focus:border-brand focus:outline-none"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={options.requireText}
          aria-label={options.requireText}
        />
      )}
    </Modal>
  )
}

/* ------------------------------------------------------------ Dropdown */

export interface MenuItem {
  label: string
  icon?: IconComponent
  onSelect: () => void
  danger?: boolean
  disabled?: boolean
  hidden?: boolean
}

export function Menu({ trigger, items, align = 'end' }: { trigger: ReactNode; items: (MenuItem | 'separator')[]; align?: 'start' | 'end' }) {
  const visible = items.filter((i) => i === 'separator' || !i.hidden)
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>{trigger}</Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align={align} sideOffset={6} collisionPadding={8} className="z-50 max-h-[min(24rem,var(--radix-dropdown-menu-content-available-height))] min-w-44 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-border bg-surface-raised p-1 shadow-md data-[state=open]:animate-scale-in">
          {visible.map((item, i) =>
            item === 'separator' ? (
              <Dropdown.Separator key={`s${i}`} className="my-1 h-px bg-border" />
            ) : (
              <Dropdown.Item
                key={item.label}
                disabled={item.disabled}
                onSelect={item.onSelect}
                className={cn(
                  'flex h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2 text-sm outline-none data-[disabled]:pointer-events-none data-[highlighted]:bg-surface-hover data-[disabled]:opacity-50',
                  item.danger ? 'text-danger' : 'text-fg',
                )}
              >
                {item.icon && <Icon icon={item.icon} size={15} className={item.danger ? '' : 'text-fg-muted'} />}
                {item.label}
              </Dropdown.Item>
            ),
          )}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  )
}

/* ------------------------------------------------------------- Tooltip */

export function Tooltip({ content, children, side = 'top' }: { content: ReactNode; children: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  if (!content) return <>{children}</>
  return (
    <RTooltip.Root delayDuration={300}>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content side={side} sideOffset={6} className="z-[60] max-w-xs rounded-md bg-fg px-2 py-1 text-xs text-fg-inverse shadow-md data-[state=delayed-open]:animate-fade-in">
          {content}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  )
}

export const TooltipProvider = RTooltip.Provider
